/**
 * @file AppRouter.jsx
 * @description This component handles all route configurations for the application,
 * including protected and public routes using React Router v5. It dynamically renders
 * a `<Header />` component based on the current path and supports lazy loading of route components.
 * Private routes are protected using a `PrivateRoute` HOC that checks for authentication.
 */

import React from 'react'
import { Route, Switch, Redirect } from 'react-router-dom'
import { PrivateRoute } from 'Utils/private-route'
import Loader from './Components/loader'
import Header from 'Components/Header'
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min'
const SwitchClinicLoader = React.lazy(() =>
  import('./Containers/Pages/SwitchClinicLoader'),
)
const Login = React.lazy(() => import('./Containers/Login'))
const FrontDesk = React.lazy(() => import('./Containers/Dashboard/FrontDesk'))
// const ChatTab = React.lazy(() => import('./Chat/ChatTab'))
const ChatContainer = React.lazy(() => import('./NewChat'))
const Settings = React.lazy(() => import('./Containers/Settings'))
// const AppointmentsTab = React.lazy(() => import('./Containers/AppointmentsTab'))
const AppointmentsTab = React.lazy(() => import('./Containers/ApptTab'))
const Report = React.lazy(() => import('./Containers/Reports'))
const Reviews = React.lazy(() => import('./Containers/Reviews'))
const AccountsModule = React.lazy(() => import('./Containers/AccountsModule'))
const Appointment = React.lazy(() =>
  import('./Components/Reports/AppointmentList'),
)
const Accounts = React.lazy(() => import('./Containers/Accounts'))
const VideoChat = React.lazy(() => import('./VideoComponents/VideoChat'))
const Page404 = React.lazy(() => import('./Containers/Pages/page404'))
const Admin = React.lazy(() => import('./Containers/Admin'))
const PriorAuthTable = React.lazy(() =>
  import('./Containers/PriorAuthModule/DataTableContainer'),
)
const PriorAuthDetails = React.lazy(() =>
  import('./Containers/PriorAuthModule/EditContainer'),
)
const ProductionDetailed = React.lazy(() => import('./Containers/ClaimsModule'))

/**
 * AppRouter
 * Main routing component for the application. Manages route-level component loading
 * and conditional rendering of the app header.
 *
 * @returns {JSX.Element} Router setup with header, fallback loader, and route switch.
 */
function AppRouter() {
  const location = useLocation()
  const pathName = location.pathname.split('/')
  return (
    <>
      {pathName[1] === 'login' ||
      pathName[1] === '' ||
      pathName[1] === 'switch-clinic' ? null : (
        <Header />
      )}
      <React.Suspense fallback={<Loader />}>
        <Switch>
          <Route
            path="/pagenotfound"
            name="/pagenotfound"
            component={Page404}
          />
          <PrivateRoute
            authed={true}
            path="/video/:id"
            name="/Video"
            component={VideoChat}
          />
          <Route
            path="/accounts/patient-list"
            name="/accountspatient-list"
            render={(props) => <Accounts {...props} />}
          />
          <Route
            path="/accounts/patient-details/"
            name="/accountspatient-details/"
            render={(props) => <AccountsModule {...props} />}
          />
          <Route
            path="/reports/appointment"
            name="/reportsappointment"
            render={(props) => <Appointment {...props} />}
          />
          <PrivateRoute
            authed={true}
            path="/reviews"
            name="reviews"
            component={Reviews}
          />
          <PrivateRoute
            authed={true}
            path="/settings"
            name="settings"
            component={Settings}
          />
          <Route
            path="/appointments"
            name="appointments"
            render={(props) => <AppointmentsTab {...props} />}
          />
          <PrivateRoute
            authed={true}
            path="/reports"
            name="reports"
            component={Report}
          />
          <Route
            path="/login"
            name="login"
            render={(props) => <Login {...props} />}
          />
          <PrivateRoute
            authed={true}
            path="/chat"
            name="chat"
            component={ChatContainer}
          />
          <PrivateRoute
            path="/dashboard"
            name="Home"
            authed={true}
            component={FrontDesk}
          />
          <PrivateRoute
            path="/admin"
            name="Home"
            authed={true}
            component={Admin}
          />
          <Route
            path="/authorizations/list"
            name="authorizations"
            render={(props) => <PriorAuthTable {...props} />}
          />
          <Route
            path="/authorizations/details"
            name="authorizations"
            render={(props) => <PriorAuthDetails {...props} />}
          />
          <Route
            path="/claims"
            name="claims"
            render={(props) => <ProductionDetailed {...props} />}
          />
          {/* <PrivateRoute
              path="/switch-clinic"
              name="switch-clinic"
              render={(props) => <SwitchClinicLoader {...props} />}
            /> */}
          <PrivateRoute
            authed={true}
            path="/switch-clinic"
            name="switch-clinic"
            component={SwitchClinicLoader}
          />

          <Redirect from="/" to="/login" />
          {/* <Redirect from="*" to="/login" /> */}
        </Switch>
      </React.Suspense>
    </>
  )
}

export default AppRouter
